/**
 * TexteRiche — le texte d'un message avec sa mise en forme : gras, italique, souligné, barré,
 * spoiler, code, citation, lien, mention, et l'emoji personnalisé (famille « conversation »,
 * 01-11-2026). Reprend le rôle de `TelegramText` du cockpit, sans rien lire de l'app : la
 * mise en forme arrive en `entites` (la forme de l'API Telegram, telle quelle — offsets en
 * unités UTF-16, comme `String.slice`), et l'image d'un emoji personnalisé vient de l'app par
 * `rendreEmoji` (sans lui, le caractère de repli que l'entité couvre).
 *
 *   <TexteRiche texte={m.text} entites={m.entities}
 *               rendreEmoji={({ id, repli, taille }) => <CustomEmoji meta={emojis.get(id)} fallback={repli} size={taille} />} />
 *
 * Un lien ou une mention se lit en blanc souligné, jamais en couleur de marque. Un spoiler se
 * découvre au clic ou au clavier (Entrée, Espace). Les URL et les @pseudos écrits en clair sans
 * entité deviennent des liens (`lienAuto`), comme les messages de bot qui n'en portent pas.
 */
import { Fragment, useState, type KeyboardEvent, type ReactNode } from "react";

import { cn } from "../utils";
import "./conversation.css";

export type TypeEntite =
  | "bold"
  | "italic"
  | "underline"
  | "strikethrough"
  | "spoiler"
  | "blockquote"
  | "code"
  | "pre"
  | "mention"
  | "text_mention"
  | "text_url"
  | "url"
  | "custom_emoji";

/** Une entité de mise en forme — la forme de l'API Telegram, passée telle quelle. */
export interface EntiteTexte {
  type: TypeEntite;
  offset: number;
  length: number;
  /** L'emoji personnalisé (`custom_emoji`) : son identifiant, en chaîne (un i64 ne tient pas dans un nombre). */
  document_id?: string | null;
  /** Le lien d'un `text_url`. */
  url?: string | null;
  /** La personne d'un `text_mention` (une mention sans pseudo). */
  user_id?: string | null;
  /** La langue d'un bloc `pre`. */
  language?: string | null;
}

/** Ce que l'app reçoit pour dessiner un emoji personnalisé. */
export interface EmojiARendre {
  id: string;
  /** Le caractère que l'entité couvre — à rendre si l'image n'est pas là. */
  repli: string;
  taille: number;
}

/** Une mention à transformer en lien : un @pseudo écrit, ou une personne sans pseudo. */
export interface MentionARelier {
  pseudo?: string;
  idUtilisateur?: string;
}

export interface TexteRicheProps {
  texte: string;
  entites?: readonly EntiteTexte[] | null;
  /** L'image d'un emoji personnalisé. Absent : le caractère de repli. */
  rendreEmoji?: (emoji: EmojiARendre) => ReactNode;
  /** Côté d'un emoji personnalisé, en px. Défaut 20. */
  tailleEmoji?: number;
  /** Les URL et les @pseudos écrits en clair deviennent des liens. Défaut `true`. */
  lienAuto?: boolean;
  /** Le lien d'une mention. Défaut : `https://t.me/<pseudo>`, `tg://user?id=<id>`. `null` = pas de lien. */
  lienMention?: (mention: MentionARelier) => string | null;
  /** Le nom du spoiler tant qu'il est caché, pour un lecteur d'écran. Défaut « Passage masqué, afficher ». */
  libelleSpoiler?: string;
  className?: string;
}

/* ── Le découpage en segments ───────────────────────────────────────────── */

/** Un morceau contigu du texte et TOUTES les entités qui le couvrent. */
export interface Segment {
  debut: number;
  fin: number;
  entites: EntiteTexte[];
}

/**
 * Coupe le texte aux bords de chaque entité : « Hello world » avec gras 0-5 et italique 3-8
 * donne 0-3 [gras], 3-5 [gras, italique], 5-8 [italique], 8-11 [].
 */
export function segmenter(texte: string, entites: readonly EntiteTexte[] = []): Segment[] {
  if (entites.length === 0) return [{ debut: 0, fin: texte.length, entites: [] }];
  const coupures = new Set<number>([0, texte.length]);
  for (const e of entites) {
    coupures.add(Math.max(0, Math.min(texte.length, e.offset)));
    coupures.add(Math.max(0, Math.min(texte.length, e.offset + e.length)));
  }
  const triees = Array.from(coupures).sort((a, b) => a - b);
  const segments: Segment[] = [];
  for (let i = 0; i < triees.length - 1; i++) {
    const debut = triees[i]!;
    const fin = triees[i + 1]!;
    if (debut === fin) continue;
    segments.push({ debut, fin, entites: entites.filter((e) => e.offset <= debut && e.offset + e.length >= fin) });
  }
  return segments;
}

/** Les identifiants des emojis personnalisés d'une liste de messages, sans doublon — ce que l'app va chercher. */
export function idsDesEmojis(messages: readonly { entities?: readonly EntiteTexte[] | null }[]): string[] {
  const ids = new Set<string>();
  for (const m of messages) {
    for (const e of m.entities ?? []) if (e.type === "custom_emoji" && e.document_id) ids.add(e.document_id);
  }
  return Array.from(ids);
}

/* ── Les liens ──────────────────────────────────────────────────────────── */

const LIEN_OU_MENTION = /(https?:\/\/[^\s]+|www\.[^\s]+|@[A-Za-z][A-Za-z0-9_]{2,31})/g;
const DEBUT_URL = /^(https?:\/\/|www\.)/;
const MENTION = /^@[A-Za-z][A-Za-z0-9_]{2,31}$/;

function mentionParDefaut({ pseudo, idUtilisateur }: MentionARelier): string | null {
  if (pseudo) return `https://t.me/${pseudo}`;
  if (idUtilisateur) return `tg://user?id=${idUtilisateur}`;
  return null;
}

function Lien({ href, mention, children }: { href: string; mention?: boolean; children: ReactNode }) {
  // un lien d'app (tg://) s'ouvre par son protocole, pas dans un onglet
  const externe = !/^[a-z]+:\/\//i.test(href) || /^https?:/i.test(href);
  return (
    <a
      href={href}
      className={mention ? "cv-mention" : "cv-lien"}
      {...(externe ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

function relierLeTexteBrut(brut: string, lienMention: (m: MentionARelier) => string | null): ReactNode[] {
  return brut.split(LIEN_OU_MENTION).map((morceau, i) => {
    if (DEBUT_URL.test(morceau)) {
      return (
        <Lien key={i} href={morceau.startsWith("www.") ? `https://${morceau}` : morceau}>
          {morceau}
        </Lien>
      );
    }
    if (MENTION.test(morceau)) {
      const href = lienMention({ pseudo: morceau.slice(1) });
      return href ? (
        <Lien key={i} href={href} mention>
          {morceau}
        </Lien>
      ) : (
        morceau
      );
    }
    return morceau;
  });
}

/* ── Le spoiler ─────────────────────────────────────────────────────────── */

function Spoiler({ children, libelle }: { children: ReactNode; libelle: string }) {
  const [vu, setVu] = useState(false);
  if (vu) return <span className="cv-spoiler" data-vu="">{children}</span>;
  const montrer = () => setVu(true);
  return (
    <span
      className="cv-spoiler"
      role="button"
      tabIndex={0}
      aria-label={libelle}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        montrer();
      }}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          montrer();
        }
      }}
    >
      <span aria-hidden>{children}</span>
    </span>
  );
}

/* ── Le composant ───────────────────────────────────────────────────────── */

export function TexteRiche({
  texte,
  entites,
  rendreEmoji,
  tailleEmoji = 20,
  lienAuto = true,
  lienMention = mentionParDefaut,
  libelleSpoiler = "Passage masqué, afficher",
  className,
}: TexteRicheProps) {
  const liste = entites ?? [];
  const segments = segmenter(texte, liste);
  return (
    <span className={cn("cv-texte", className)}>
      {segments.map((s, i) => {
        const brut = texte.slice(s.debut, s.fin);

        const emoji = s.entites.find((e) => e.type === "custom_emoji" && e.document_id);
        if (emoji?.document_id) {
          return (
            <span key={i} className="cv-emoji" role="img" aria-label={brut} style={{ width: tailleEmoji, height: tailleEmoji }}>
              {rendreEmoji ? rendreEmoji({ id: emoji.document_id, repli: brut, taille: tailleEmoji }) : brut}
            </span>
          );
        }

        const lien = s.entites.find((e) => e.type === "text_url" || e.type === "url" || e.type === "mention" || e.type === "text_mention");
        let rendu: ReactNode = lienAuto && !lien ? relierLeTexteBrut(brut, lienMention) : brut;

        for (const e of s.entites) {
          switch (e.type) {
            case "bold":
              rendu = <strong>{rendu}</strong>;
              break;
            case "italic":
              rendu = <em>{rendu}</em>;
              break;
            case "underline":
              rendu = <u>{rendu}</u>;
              break;
            case "strikethrough":
              rendu = <s>{rendu}</s>;
              break;
            case "code":
              rendu = <code className="cv-code">{rendu}</code>;
              break;
            case "pre":
              rendu = (
                <span className="cv-bloc-code" data-langue={e.language ?? undefined}>
                  <code>{rendu}</code>
                </span>
              );
              break;
            case "blockquote":
              rendu = <span className="cv-citation-texte">{rendu}</span>;
              break;
            case "spoiler":
              rendu = <Spoiler libelle={libelleSpoiler}>{rendu}</Spoiler>;
              break;
            default:
              break;
          }
        }

        if (lien) {
          let href: string | null = null;
          if (lien.type === "text_url") href = lien.url ?? null;
          else if (lien.type === "url") href = brut.startsWith("www.") ? `https://${brut}` : brut;
          else if (lien.type === "mention") href = lienMention({ pseudo: brut.replace(/^@/, "") });
          else if (lien.type === "text_mention" && lien.user_id) href = lienMention({ idUtilisateur: lien.user_id });
          if (href) {
            rendu = (
              <Lien href={href} mention={lien.type === "mention" || lien.type === "text_mention"}>
                {rendu}
              </Lien>
            );
          }
        }

        return <Fragment key={i}>{rendu}</Fragment>;
      })}
    </span>
  );
}
